/**
 * PortalPaymentComplete - Payment confirmation page after Square redirect
 * Deployed to: frontend/src/pages/portal/PortalPaymentComplete.js
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, AlertCircle, ArrowLeft, FileText } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const PortalPaymentComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const lookupPayment = async () => {
      try {
        const transactionId = searchParams.get('transactionId');
        const orderId = searchParams.get('orderId');
        const referenceId = searchParams.get('referenceId');

        const data = await clientPortalService.getPaymentComplete(
          transactionId, orderId, referenceId
        );
        setResult(data);
      } catch (err) {
        setError('Unable to confirm payment. Please check your invoices for the latest status.');
      } finally {
        setLoading(false);
      }
    };

    lookupPayment();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4" />
          <p className="text-virgo-teal-400">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8 text-center">
          {error ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
                <AlertCircle className="w-8 h-8 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Status Unknown</h1>
              <p className="text-virgo-teal-400 mb-6">{error}</p>
            </>
          ) : result?.pending ? (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/20 mb-4">
                <Clock className="w-8 h-8 text-cyan-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
              <p className="text-virgo-teal-400 mb-6">{result.message}</p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Payment Confirmed</h1>
              <p className="text-virgo-teal-400 mb-6">Thank you for your payment!</p>

              <div className="bg-virgo-navy-600/50 rounded-lg p-4 mb-6 space-y-2">
                {result?.amount && (
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Amount</span>
                    <span className="text-white font-bold">${result.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {result?.invoice_number && (
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Invoice</span>
                    <span className="text-white">{result.invoice_number}</span>
                  </div>
                )}
                {result?.payment_date && (
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Date</span>
                    <span className="text-white">{new Date(result.payment_date).toLocaleDateString()}</span>
                  </div>
                )}
                {result?.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-virgo-teal-400">Reference</span>
                    <span className="text-white text-xs font-mono">{result.transaction_id.slice(0, 12)}...</span>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex flex-col gap-3">
            <Link
              to="/portal/invoices"
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all flex items-center justify-center gap-2"
            >
              <FileText size={18} />
              Back to Invoices
            </Link>
            <Link
              to="/portal/dashboard"
              className="w-full py-3 bg-virgo-navy-600 text-virgo-teal-300 font-semibold rounded-lg hover:bg-virgo-navy-500 transition-all flex items-center justify-center gap-2"
            >
              <ArrowLeft size={18} />
              Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalPaymentComplete;
